import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import {
  FUND_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE,
  FUND_INVESTMENTS_BY_MD_QUERY_KEY,
  SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/funds';
import { FundService } from 'services';
import { throwError } from 'utilities';
import {
  GetFundInvestmentsByMDParams,
  GetFundInvestmentsByMDResponse,
  GetFundInvestmentsByMDReturn,
  UseGetFundInvestmentsByMDParams,
} from './useGetFundInvestmentsByMD.types';

const getFundInvestmentsByMD = async (
  params: GetFundInvestmentsByMDParams
): Promise<GetFundInvestmentsByMDResponse | null> => {
  const { fundId, measurementDateId, checkForExport = false } = params;

  if (isNil(fundId) && isNil(measurementDateId)) {
    return null;
  }

  const fundService = new FundService();

  try {
    const fundInvestments = (await fundService.getFundInvestmentByMD(
      toString(fundId),
      toString(measurementDateId),
      checkForExport
    )) as GetFundInvestmentsByMDResponse;

    if (!fundInvestments) {
      return throwError(FUND_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE);
    }

    return fundInvestments;
  } catch (error) {
    return throwError(FUND_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFundInvestmentsByMD = (params: UseGetFundInvestmentsByMDParams): GetFundInvestmentsByMDReturn => {
  const { fund, measurementDate, shouldQueryAutomatically = true } = params;

  const fundId = fund?.id;
  const measurementDateId = measurementDate?.id;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetFundInvestmentsByMDReturn['data'],
    Error
  >({
    enabled: !isNil(fundId) && !isNil(measurementDateId) && shouldQueryAutomatically,
    queryFn: () => getFundInvestmentsByMD({ fundId, measurementDateId, checkForExport: false }),
    queryKey: [FUND_INVESTMENTS_BY_MD_QUERY_KEY, fundId, measurementDateId],
    refetchOnWindowFocus: SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export { useGetFundInvestmentsByMD, getFundInvestmentsByMD };
